/* Public WordPress feeds only; no changes to product, cart, or checkout behavior. */
(() => {
  if (customElements.get('defrag-zine-feed')) return;
  const pending = new Map();
  const ttl = 10 * 60 * 1000;
  const base = '/a/zine/wp-json/wp/v2/posts/';
  const plain = (html) => {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    doc.querySelectorAll('script,style,.read-more').forEach((node) => node.remove());
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  };
  const safeUrl = (value, image = false) => {
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:') return '';
      if (!['zine.odb-c.net', 'ec.odb-c.net'].includes(url.hostname)) return '';
      if (image) return /\/wp-content\/uploads\//.test(url.pathname) ? url.href : '';
      if (url.hostname === 'zine.odb-c.net') return '/a/zine' + url.pathname + url.search + url.hash;
      return url.pathname.startsWith('/a/zine/') ? url.pathname + url.search + url.hash : '';
    } catch (_) { return ''; }
  };
  const normalize = (post) => {
    const media = post._embedded?.['wp:featuredmedia']?.[0];
    const sizes = media?.media_details?.sizes;
    const image = sizes?.medium_large?.source_url || sizes?.medium?.source_url || media?.source_url;
    return {
      title: plain(post.title?.rendered).slice(0, 220),
      excerpt: plain(post.excerpt?.rendered).slice(0, 180),
      url: safeUrl(post.link), image: safeUrl(image, true),
      date: /^\d{4}-\d{2}-\d{2}/.test(post.date || '') ? post.date.slice(0, 10) : '',
      label: post.categories?.includes(8) ? 'CAT NOTES' : 'JOURNAL'
    };
  };
  function valid(items) {
    return Array.isArray(items) && items.length > 0 && items.length <= 3 && items.every((item) =>
      typeof item.title === 'string' && item.title.length > 0 && safeUrl(item.url) &&
      typeof item.excerpt === 'string' && typeof item.date === 'string' &&
      typeof item.label === 'string' && typeof item.image === 'string' && (!item.image || safeUrl(item.image, true)));
  }
  function fetchFeed(kind) {
    if (pending.has(kind)) return pending.get(kind);
    const key = 'defrag-zine:v1:' + kind;
    const promise = (async () => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (saved && Date.now() - saved.time < ttl && valid(saved.items)) return saved.items;
      } catch (_) { /* Storage is optional. */ }
      const params = new URLSearchParams({per_page: '3', status: 'publish', orderby: 'date', order: 'desc',
        _embed: 'wp:featuredmedia', _fields: 'id,link,date,title,excerpt,categories,_links,_embedded'});
      if (kind === 'notes') params.set('categories', '8');
      else { params.set('categories', '1'); params.set('categories_exclude', '8'); }
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 7000);
      try {
        const response = await fetch(base + '?' + params, {signal: controller.signal, credentials: 'omit'});
        if (!response.ok) throw new Error('Journal unavailable');
        const posts = await response.json();
        if (!Array.isArray(posts)) throw new Error('Invalid journal response');
        const items = posts.map(normalize).filter((item) => item.title && item.url);
        if (!valid(items)) throw new Error('Empty journal response');
        try { sessionStorage.setItem(key, JSON.stringify({time: Date.now(), items})); } catch (_) { /* Optional. */ }
        return items;
      } finally { clearTimeout(timer); }
    })();
    pending.set(kind, promise);
    return promise;
  }
  function node(tag, className, value) {
    const element = document.createElement(tag);
    element.className = className;
    if (value) element.textContent = value;
    return element;
  }
  function card(item) {
    const link = node('a', 'dfz-card');
    link.href = safeUrl(item.url);
    const picture = node('span', 'dfz-card__image');
    if (item.image) {
      const image = document.createElement('img');
      image.src = safeUrl(item.image, true); image.alt = ''; image.width = 768; image.height = 512;
      image.loading = 'lazy'; image.decoding = 'async';
      image.addEventListener('error', () => picture.replaceChildren(node('span', 'dfz-card__placeholder', 'DEFRAG ZINE.')), {once: true});
      picture.append(image);
    } else picture.append(node('span', 'dfz-card__placeholder', 'DEFRAG ZINE.'));
    const body = node('span', 'dfz-card__body');
    const meta = node('span', 'dfz-card__meta');
    const date = node('time', '', item.date.replaceAll('-', '.')); date.dateTime = item.date;
    meta.append(node('span', '', item.label), date);
    body.append(meta, node('span', 'dfz-card__title', item.title), node('span', 'dfz-card__excerpt', item.excerpt), node('span', 'dfz-card__read', '読む ↗'));
    link.append(picture, body);
    return link;
  }
  customElements.define('defrag-zine-feed', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.state) return;
      this.dataset.state = 'waiting';
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
        }, {rootMargin: '250px'});
        this.observer.observe(this);
      } else this.load();
    }
    disconnectedCallback() { this.observer?.disconnect(); if (this.dataset.state === 'waiting') delete this.dataset.state; }
    async load() {
      this.dataset.state = 'loading';
      try {
        const kind = this.dataset.feed;
        let items;
        if (kind === 'mixed') {
          const results = await Promise.allSettled([fetchFeed('stories'), fetchFeed('notes')]);
          items = [...(results[0].status === 'fulfilled' ? results[0].value.slice(0, 2) : []),
            ...(results[1].status === 'fulfilled' ? results[1].value.slice(0, 1) : [])];
          if (items.length < 3) throw new Error('Keep complete fallback');
        } else items = await fetchFeed(kind === 'notes' ? 'notes' : 'stories');
        if (!this.isConnected) { delete this.dataset.state; return; }
        // Do not replace a card while the visitor is using its keyboard focus.
        if (!this.contains(document.activeElement)) this.replaceChildren(...items.slice(0, 3).map(card));
        this.dataset.state = 'ready';
      } catch (_) { this.dataset.state = 'fallback'; }
    }
  });
})();
